// @flow
import React, { type Node, Fragment } from "react";
import { get, flowRight as compose } from "lodash";
import { connect } from "react-redux";
import { graphql } from "@apollo/client/react/hoc";
import { mapDispatchToProps, TracksComponent } from "@tvg/tracks-builder";
import {
  type Track,
  type TracksTypes
} from "@tvg/tracks-builder/src/TracksType/types";
import mediator from "@tvg/mediator";
import filtersService from "@tvg/tracks-header/service";
import TrackSplitter from "@fdr/atomic-ui/_atoms/TrackSplitter";
import { onOpenComplianceModal } from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";
import { isAccountCompliantSelector } from "@tvg/sh-utils/sessionUtils";
import { getAccountNumber } from "@urp/store-selectors";
import TracksComponents from "./TracksType/types";
import getTracksType from "./TracksType";
import ExpandableTrack from "./components/expandable-track";
import type { TrackComponentFDRProps } from "./TracksType/types";

export class TracksBuilder extends TracksComponent {
  constructor(props: TrackComponentFDRProps) {
    super(props);
    this.state = {
      firstRender: true,
      innerUpdate: false,
      favorites: {},
      forceRefetch: false
    };
    this.TracksObject = getTracksType(props.trackType);
  }

  handleClearFilters = () => {
    filtersService.setRaceFilterPreference([]);
    filtersService.setRegionFilterPreference([]);
    this.props.clearRaceFilters();
    this.props.clearRegionFilters();
  };

  signalLoadedComponent = () => {
    if (
      (!this.props.isLoading && this.props.shouldUpdate) ||
      get(this.props, "tracks.length") ||
      get(this.props, "featuredTracks.length.length") ||
      !(this.props.isLoading && this.state.firstRender)
    ) {
      this.props.signalLoadedComponent(this.props.trackType);
    }
  };

  handleFavoriteChange = (
    isAdding: boolean,
    trackCode: string
  ): Promise<void> => {
    const isAccountCompliant = get(this.props, "isAccountCompliant", false);

    const isBaseTracks =
      (this.props.isLogged && isAccountCompliant) ||
      !this.TracksObject.usesRDA ||
      this.props.trackType === TracksComponents.TRACKS_AZ ||
      this.props.trackType === TracksComponents.RESULTED_TRACKS_AZ;

    const allTracks = isBaseTracks
      ? this.props.tracks
      : this.props.featuredTracks;

    const track = allTracks.find(
      (tempTrack: Track) => tempTrack.code === trackCode
    );

    if (track && this.props.isLogged && !isAccountCompliant) {
      onOpenComplianceModal();
      return this.removeTrackFromFavorites(track);
    }
    if (track && isAdding && this.props.isLogged) {
      return this.addTrackToFavorites(track);
    }
    if (track && this.props.isLogged) {
      return this.removeTrackFromFavorites(track);
    }
    if (track) {
      mediator.base.dispatch({
        type: "OPEN_LOGIN",
        payload: {
          callback: () => {
            this.addTrackToFavorites(track);
          }
        }
      });
    }

    return Promise.resolve();
  };

  renderExpandableTrack = (track: Track, index: number): Node => {
    const trackChar = track.name.charAt(0);

    const isAccountCompliant = get(this.props, "isAccountCompliant", false);

    const isBaseTracks =
      (this.props.isLogged && isAccountCompliant) ||
      !this.TracksObject.usesRDA ||
      this.props.trackType === TracksComponents.TRACKS_AZ ||
      this.props.trackType === TracksComponents.RESULTED_TRACKS_AZ;

    const isLastTrack = isBaseTracks
      ? this.props.tracks.length - 1 === index
      : this.props.featuredTracks.length - 1 === index;

    const expandProps = {
      device: this.props.device,
      key: `track${track.code}`,
      trackType: this.props.trackType,
      idPrefix: this.props.trackType,
      track,
      openTracks: get(this.props, "openTracks", []),
      handleFavoriteChange: this.handleFavoriteChange,
      onTrackClickHandler: this.onTrackClickHandler,
      trackDropdownIsOpen: this.trackDropdownIsOpen,
      shouldUpdate: this.props.shouldUpdate,
      accountId: this.props.accountId,
      rdaClient: this.props.rdaClient,
      isFavorite: get(track, "isFavorite", false),
      isLogged: this.props.isLogged,
      isLastTrack
    };
    const expandableTrack = (
      <ExpandableTrack
        {...expandProps}
        storedFavoriteTracks={this.props.storedFavoriteTracks}
        useIsPromoTagShownFlag={this.props.useIsPromoTagShownFlag}
      />
    );

    if (this.TracksObject.renderTracksLetters) {
      if (this.currentTrackChar !== trackChar || index === 0) {
        const inState = this.lettersRendered.indexOf(trackChar);
        if (inState === -1) {
          this.lettersRendered.push(trackChar);
          this.currentTrackChar = trackChar;
          return (
            <Fragment key={`track${track.code}`}>
              <TrackSplitter letter={trackChar} />
              {expandableTrack}
            </Fragment>
          );
        }
      }
    }
    return expandableTrack;
  };

  renderTracks = (): Node[] => {
    const isAccountCompliant = get(this.props, "isAccountCompliant", false);

    const isBaseTracks =
      (this.props.isLogged && isAccountCompliant) ||
      !this.TracksObject.usesRDA ||
      this.props.trackType === TracksComponents.TRACKS_AZ ||
      this.props.trackType === TracksComponents.RESULTED_TRACKS_AZ;

    const allTracks = isBaseTracks
      ? this.props.tracks
      : this.props.featuredTracks;

    return allTracks.map((track: Track, index: number) =>
      this.renderExpandableTrack(track, index)
    );
  };

  render() {
    const Template = this.TracksObject.template;
    this.lettersRendered = [];
    return (
      <Template
        device={this.props.device}
        templateTitle={this.props.title}
        hasError={this.props.hasError}
        onClearFilters={this.handleClearFilters}
        numberOfFavorites={this.props.storedFavoriteTracks.length}
        hasActiveFilters={
          this.props.regionFilters.length || this.props.raceTypeFilters.length
        }
        isLoading={
          this.props.isLoading ||
          (this.state.firstRender && !this.props.tracks && !this.props.hasError)
        }
        isLogged={this.props.isLogged}
        tracks={this.props.tracks}
        showHeader={this.props.showHeader}
      >
        {this.props.trackType === TracksComponents.TOP_TRACKS
          ? this.renderTracks().slice(0, 10)
          : this.renderTracks()}
      </Template>
    );
  }
}

const getSkipQuery = (props: TrackComponentFDRProps) =>
  getTracksType(props.trackType).skipQuery(props);

const getSkipRDAQuery = (props: TrackComponentFDRProps) =>
  getTracksType(props.trackType).skipRDAQuery(props);

const getQuery = (trackType: TracksTypes) => {
  const component = getTracksType(trackType);
  return component.onlyUsesRDA ? component.rdaQuery : component.query;
};

const getRDAQuery = (trackType: TracksTypes) => {
  const component = getTracksType(trackType);
  return component.usesRDA ? component.rdaQuery : component.query;
};

const getApolloOptions = (props: TrackComponentFDRProps) =>
  getTracksType(props.trackType).graphOptions.options(props);

const getApolloProps = (props: TrackComponentFDRProps) =>
  getTracksType(get(props, "ownProps.trackType")).graphOptions.props(props);

export const mapStateToProps = (store: *) => ({
  wagerProfile: get(store, "userData.user.profile", "PORT-Generic"),
  openTracks: get(store, "topTracks.openTracks"),
  isLogged: get(store, "userData.logged", false),
  isAccountCompliant: isAccountCompliantSelector(store),
  accountId: getAccountNumber(store),
  raceTypeFilters: get(store, "raceFilters.raceTypeFilters"),
  regionFilters: get(store, "raceFilters.regionFilters"),
  amountOfFavoriteTracks: get(store, "userFavorites.tracks.length", 0),
  storedFavoriteTracks: get(store, "userFavorites.tracks", []),
  useIsPromoTagShownFlag: get(
    store,
    "capi.featureToggles.useIsPromoTagShownFlag",
    false
  )
});

const connectAnComposeTracksComponent = (trackType: TracksTypes) =>
  connect(
    /* istanbul ignore next */
    mapStateToProps,
    /* istanbul ignore next */
    mapDispatchToProps
  )(
    compose(
      graphql(getQuery(trackType), {
        options: getApolloOptions,
        props: getApolloProps,
        skip: getSkipQuery
      }),
      graphql(getRDAQuery(trackType), {
        options: getApolloOptions,
        props: getApolloProps,
        skip: getSkipRDAQuery
      })
    )(TracksBuilder)
  );

export const TopTracks = connectAnComposeTracksComponent(
  TracksComponents.TOP_TRACKS
);
export const TracksAZ = connectAnComposeTracksComponent(
  TracksComponents.TRACKS_AZ
);
export const FavoriteTracks = connectAnComposeTracksComponent(
  TracksComponents.FAVORITE_TRACKS
);

export const ResultedTracksAZ = connectAnComposeTracksComponent(
  TracksComponents.RESULTED_TRACKS_AZ
);

export const ResultedFavoriteTracks = connectAnComposeTracksComponent(
  TracksComponents.RESULTED_FAVORITE_TRACKS
);
